import { Injectable } from '@nestjs/common';
import { JSONSchema } from '../interfaces/capability-config.interface';

/**
 * 类型到默认值的映射
 */
const TYPE_DEFAULT_VALUES: Record<string, unknown> = {
  array: [],
  object: {},
  string: '',
  number: 0,
  integer: 0,
  boolean: false,
  null: null,
};

/**
 * 模板引擎服务
 *
 * 支持语法：
 * - expr: '{{' + selector + '}}'
 * - selector: 'input.' + ident | selector.ident
 * - ident: [a-zA-Z_]([a-zA-Z_0-9])*
 *
 * 示例：
 * - {{input.a}}
 * - {{input.a.b}}
 * - "this is {{input.a.b}}"
 *
 * 求值规则：
 * - 如果整个字符串是单个表达式，保留原始类型
 * - 如果是字符串插值（多个表达式或混合内容），返回字符串
 * - 如果变量不存在：
 *   - 未传 schema：返回原始表达式
 *   - 传了 schema（整串表达式）：按 schema 返回默认值
 *   - 传了 schema（字符串插值）：保留原始表达式
 */
@Injectable()
export class TemplateEngineService {
  // 匹配 {{input.xxx}} 或 {{input.xxx.yyy}} 表达式
  private readonly EXPR_REGEX =
    /\{\{\s*input\.([a-zA-Z_][a-zA-Z_0-9]*(?:\.[a-zA-Z_][a-zA-Z_0-9]*)*\s*)\}\}/g;
  // 匹配整串单个表达式
  private readonly WHOLE_STRING_EXPR_REGEX =
    /^\{\{\s*input\.([a-zA-Z_][a-zA-Z_0-9]*(?:\.[a-zA-Z_][a-zA-Z_0-9]*)*)\s*\}\}$/;

  /**
   * 解析 formValue 模板
   * @param template - formValue 模板对象
   * @param input - 用户输入参数
   * @param paramsSchema - 可选，输入参数的 JSON Schema，用于推断默认值
   * @returns 解析后的参数对象
   */
  resolve(
    template: unknown,
    input: Record<string, unknown>,
    paramsSchema?: JSONSchema,
  ): unknown {
    if (typeof template === 'string') {
      return this.resolveString(template, input, paramsSchema);
    }

    if (Array.isArray(template)) {
      return template.map(item => this.resolve(item, input, paramsSchema));
    }

    if (template !== null && typeof template === 'object') {
      return this.resolveObject(
        template as Record<string, unknown>,
        input,
        paramsSchema,
      );
    }

    return template;
  }

  private resolveString(
    template: string,
    input: Record<string, unknown>,
    paramsSchema?: JSONSchema,
  ): unknown {
    // 情况1: 整串是单个表达式 → 保留原始类型
    const wholeMatch = template.match(this.WHOLE_STRING_EXPR_REGEX);
    if (wholeMatch) {
      const path = wholeMatch[1];
      const value = this.getValueByPath(input, path);

      // 变量存在，返回值
      if (value !== undefined) {
        return value;
      }

      // 变量不存在，尝试从 schema 获取默认值
      if (paramsSchema) {
        const defaultValue = this.getDefaultValueForPath(path, paramsSchema);
        if (defaultValue !== undefined) {
          return defaultValue;
        }
      }

      // 回退：返回原始表达式
      return template;
    }

    // 情况2: 字符串插值 → 返回字符串（不使用 schema 默认值）
    // 重置正则的 lastIndex（因为使用了 g 标志）
    this.EXPR_REGEX.lastIndex = 0;

    // 检查是否有任何表达式
    if (!this.EXPR_REGEX.test(template)) {
      return template;
    }

    // 重置并进行替换
    this.EXPR_REGEX.lastIndex = 0;
    const result = template.replace(this.EXPR_REGEX, (match, path) => {
      const value = this.getValueByPath(input, path);
      // 变量不存在，保留原始表达式
      if (value === undefined) {
        return match;
      }
      return String(value);
    });

    return result;
  }

  private resolveObject(
    template: Record<string, unknown>,
    input: Record<string, unknown>,
    paramsSchema?: JSONSchema,
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(template)) {
      result[key] = this.resolve(value, input, paramsSchema);
    }

    return result;
  }

  private getValueByPath(obj: Record<string, unknown>, path: string): unknown {
    const keys = path.split('.');
    let current: unknown = obj;

    for (const key of keys) {
      if (current === null || current === undefined) {
        return undefined;
      }
      current = (current as Record<string, unknown>)[key];
    }

    return current;
  }

  /**
   * 根据路径从 schema 获取默认值
   * @param path - 变量路径，如 "a.b.c"
   * @param schema - JSON Schema
   * @returns 默认值，如果无法确定则返回 undefined
   */
  private getDefaultValueForPath(
    path: string,
    schema: JSONSchema,
  ): unknown {
    const fieldSchema = this.getSchemaForPath(path, schema);
    if (!fieldSchema) {
      return undefined;
    }
    return this.getDefaultValueFromSchema(fieldSchema);
  }

  /**
   * 根据路径查找对应的 schema 定义
   * @param path - 变量路径，如 "a.b.c"
   * @param schema - 根 JSON Schema
   * @returns 路径对应的 schema，如果不存在则返回 undefined
   */
  private getSchemaForPath(
    path: string,
    schema: JSONSchema,
  ): JSONSchema | undefined {
    const keys = path.split('.');
    let currentSchema: JSONSchema | undefined = schema;

    for (const key of keys) {
      if (!currentSchema?.properties) {
        return undefined;
      }
      currentSchema = currentSchema.properties[key];
      if (!currentSchema) {
        return undefined;
      }
    }

    return currentSchema;
  }

  /**
   * 从 schema 获取默认值
   * 优先级：default > type > undefined
   * @param schema - 字段的 JSON Schema
   * @returns 默认值
   */
  private getDefaultValueFromSchema(schema: JSONSchema): unknown {
    // 优先使用 schema 中定义的 default
    if (schema.default !== undefined) {
      return schema.default;
    }

    // 如果有 oneOf 或 anyOf，无法确定类型，返回 undefined
    if (schema.oneOf || schema.anyOf) {
      return undefined;
    }

    // 根据 type 返回默认值
    const type = schema.type;
    if (!type) {
      return undefined;
    }

    // 联合类型：取第一个类型的默认值
    if (Array.isArray(type)) {
      for (const t of type) {
        if (t in TYPE_DEFAULT_VALUES) {
          return TYPE_DEFAULT_VALUES[t];
        }
      }
      return undefined;
    }

    // 单一类型
    return TYPE_DEFAULT_VALUES[type];
  }
}
